/**
 * CognaraLogo — official Cognara Axis-Mundi lockup.
 *
 * The icon mark (tri-line + ember axis-mundi) is shared with the master brand
 * SVGs in /public/brand/. Light/dark mode automatically picks the right
 * wordmark asset.
 *
 * Variants:
 *   • 'icon'    — square icon-only mark (defaults 28px). Theme-able via `color`.
 *   • 'compact' — icon + "Cognara" wordmark (from uploaded SVG).
 *   • 'full'    — same as compact (kept for backward-compat).
 *   • 'wordmark' — wordmark only (no icon mark).
 *
 * Props:
 *   • theme: 'auto' (default) | 'light' | 'dark' — selects which uploaded SVG.
 *            'auto' picks 'dark' when the document root carries the `dark` class.
 */

const EMBER = '#F26B1F';

const resolveTheme = (theme) => {
  if (theme === 'light' || theme === 'dark') return theme;
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  return 'light';
};

export const CognaraLogo = ({
  variant = 'icon',
  size,
  theme = 'auto',
  className = '',
  'aria-label': ariaLabel = 'Cognara',
  ...rest
}) => {
  // Icon variant — keep the original Axis-Mundi mark so callsites with tight
  // 14–32px slots (chat avatars, nav, login) keep working unchanged.
  if (variant === 'icon') {
    const s = size || 28;
    const lineColor = 'currentColor';
    return (
      <svg
        viewBox="0 0 140 140"
        width={s}
        height={s}
        className={className}
        aria-label={ariaLabel}
        role="img"
        {...rest}
      >
        <g fill="none" strokeLinecap="round">
          <line x1="25" y1="45" x2="115" y2="45" stroke={lineColor} strokeWidth="2.5" opacity="0.9" />
          <line x1="25" y1="70" x2="115" y2="70" stroke={lineColor} strokeWidth="2.5" opacity="0.75" />
          <line x1="25" y1="95" x2="115" y2="95" stroke={lineColor} strokeWidth="2.5" opacity="0.6" />
          <line x1="70" y1="20" x2="70" y2="120" stroke={EMBER} strokeWidth="3" />
        </g>
        <circle cx="70" cy="45" r="4" fill={EMBER} />
        <circle cx="70" cy="70" r="4" fill={EMBER} />
        <circle cx="70" cy="95" r="4" fill={EMBER} />
      </svg>
    );
  }

  // Compact / full / wordmark — use the uploaded master SVGs (light/dark).
  const t = resolveTheme(theme);
  const src = `/brand/cognara-${t}.svg`;
  // Master SVG viewBox is 1300x280 (~4.64:1). Default height 40.
  const h = size || 40;
  const w = Math.round(h * (1300 / 280));
  return (
    <img
      src={src}
      alt={ariaLabel}
      height={h}
      width={w}
      style={{ height: h, width: 'auto', maxWidth: w, display: 'inline-block' }}
      className={className}
      {...rest}
    />
  );
};

export default CognaraLogo;
